/**
 * /payroll-l2/expenses/new — Create a new expense report.
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import DeleteIcon from "@mui/icons-material/Delete";

import { api } from "@/lib/api";
import { HelpHint } from "@/components/HelpHint";

export const Route = createFileRoute("/_auth/payroll-l2/expenses_/new")({
  component: NewExpenseReportPage,
});

interface LineForm {
  expense_date: string;
  description: string;
  amount: string;
  currency_code: string;
  receipt_url: string;
}

function blankLine(): LineForm {
  return { expense_date: new Date().toISOString().split("T")[0], description: "", amount: "", currency_code: "USD", receipt_url: "" };
}

function NewExpenseReportPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const today = new Date().toISOString().split("T")[0];

  const [form, setForm] = useState({ report_date: today, notes: "" });
  const [lines, setLines] = useState<LineForm[]>([blankLine()]);
  const [serverError, setServerError] = useState<string | null>(null);
  const [ocrLoading, setOcrLoading] = useState<boolean[]>(() => [false]);
  const fileInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const createMut = useMutation({
    mutationFn: async () => {
      const body = {
        report_date: form.report_date,
        notes: form.notes || null,
        lines: lines.map((l) => ({
          expense_date: l.expense_date,
          description: l.description,
          amount: l.amount,
          currency_code: l.currency_code,
          receipt_url: l.receipt_url || null,
        })),
      };
      return await api("/api/payroll-l2/expense-reports", { method: "POST", body });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payroll-l2", "expenses"] });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      navigate({ to: "/payroll-l2/expenses" as any });
    },
    onError: (err: unknown) => {
      setServerError(err instanceof Error ? err.message : "An error occurred");
    },
  });

  const ocrMut = useMutation({
    mutationFn: async ({ idx: _idx, dataUri, mimeType }: { idx: number; dataUri: string; mimeType: string }) => {
      const base64 = dataUri.includes(",") ? dataUri.split(",", 2)[1] : dataUri;
      return await api<{
        vendor_name: string | null;
        amount: string | null;
        currency_code: string | null;
        expense_date: string | null;
        description: string | null;
      }>("/api/payroll-l2/expense-lines/ocr", {
        method: "POST",
        body: { image_base64: base64, mime_type: mimeType },
      });
    },
    onSuccess: (data, { idx }) => {
      setLines((prev) =>
        prev.map((l, i) => {
          if (i !== idx) return l;
          return {
            ...l,
            ...(data.description ? { description: data.description } : {}),
            ...(data.amount ? { amount: data.amount } : {}),
            ...(data.currency_code ? { currency_code: data.currency_code } : {}),
            ...(data.expense_date ? { expense_date: data.expense_date } : {}),
          };
        })
      );
      setOcrLoading((prev) => prev.map((v, i) => (i === idx ? false : v)));
    },
    onError: (_err, { idx: failedIdx }) => {
      setOcrLoading((prev) => prev.map((v, i) => (i === failedIdx ? false : v)));
    },
  });

  const handleReceiptFile = (idx: number, file: File) => {
    if (file.size > 5 * 1024 * 1024) {
      setServerError("Receipt image must be under 5 MB.");
      return;
    }
    if (!file.type.startsWith("image/")) {
      setServerError("Receipt must be an image file.");
      return;
    }
    setServerError(null);
    const reader = new FileReader();
    reader.onload = () => {
      setOcrLoading((prev) => prev.map((v, i) => (i === idx ? true : v)));
      ocrMut.mutate({ idx, dataUri: reader.result as string, mimeType: file.type });
    };
    reader.onerror = () => {
      setServerError("Could not read the receipt image.");
      setOcrLoading((prev) => prev.map((v, i) => (i === idx ? false : v)));
    };
    reader.readAsDataURL(file);
  };

  const updateLine = (idx: number, field: keyof LineForm, value: string) => {
    setLines((prev) => prev.map((l, i) => i === idx ? { ...l, [field]: value } : l));
  };

  const removeLine = (idx: number) => {
    setLines((prev) => prev.filter((_, i) => i !== idx));
    setOcrLoading((prev) => prev.filter((_, i) => i !== idx));
  };

  const total = lines.reduce((s, l) => s + (Number(l.amount) || 0), 0);
  const canSubmit = form.report_date && lines.every((l) => l.description && l.amount && l.expense_date);

  return (
    <Box maxWidth={720}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h5" fontWeight={600}>New Expense Report</Typography>
        <HelpHint docPath="payroll-l2/expenses.md" label="New expense help" />
      </Stack>

      {serverError && <Alert severity="error" sx={{ mb: 2 }}>{serverError}</Alert>}

      <Stack spacing={2} mb={3}>
        <TextField
          label="Report Date"
          type="date"
          value={form.report_date}
          onChange={(e) => setForm((f) => ({ ...f, report_date: e.target.value }))}
          InputLabelProps={{ shrink: true }}
          required
        />
        <TextField
          label="Notes"
          value={form.notes}
          onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
          multiline
          rows={2}
        />
      </Stack>

      <Divider sx={{ mb: 2 }} />
      <Typography variant="subtitle1" fontWeight={500} mb={1}>Line Items</Typography>

      <Stack spacing={2} mb={2}>
        {lines.map((line, idx) => (
          <Box key={idx} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 2 }}>
            <input
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              ref={(el) => { fileInputRefs.current[idx] = el; }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleReceiptFile(idx, file);
                e.target.value = "";
              }}
            />
            <Stack direction="row" gap={1} flexWrap="wrap" alignItems="flex-start">
              <TextField
                label="Date"
                type="date"
                size="small"
                value={line.expense_date}
                onChange={(e) => updateLine(idx, "expense_date", e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
                sx={{ width: 160 }}
              />
              <TextField
                label="Description"
                size="small"
                value={line.description}
                onChange={(e) => updateLine(idx, "description", e.target.value)}
                required
                sx={{ flex: 2, minWidth: 200 }}
              />
              <TextField
                label="Amount"
                size="small"
                type="number"
                value={line.amount}
                onChange={(e) => updateLine(idx, "amount", e.target.value)}
                required
                sx={{ width: 120 }}
                inputProps={{ step: "0.01" }}
              />
              <TextField
                label="Receipt URL"
                size="small"
                value={line.receipt_url}
                onChange={(e) => updateLine(idx, "receipt_url", e.target.value)}
                sx={{ flex: 1, minWidth: 160 }}
              />
              <Button
                size="small"
                variant="outlined"
                startIcon={ocrLoading[idx] ? undefined : <AutoAwesomeIcon fontSize="small" />}
                onClick={() => fileInputRefs.current[idx]?.click()}
                disabled={ocrLoading[idx]}
                sx={{ whiteSpace: "nowrap", alignSelf: "flex-start", mt: "2px" }}
              >
                {ocrLoading[idx] ? "Reading…" : "Read receipt"}
              </Button>
              <IconButton
                size="small"
                color="error"
                onClick={() => removeLine(idx)}
                disabled={lines.length <= 1}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Box>
        ))}
      </Stack>

      <Button
        startIcon={<AddIcon />}
        size="small"
        onClick={() => {
          setLines((prev) => [...prev, blankLine()]);
          setOcrLoading((prev) => [...prev, false]);
        }}
        sx={{ mb: 3 }}
      >
        Add Line
      </Button>

      <Divider sx={{ mb: 2 }} />
      <Typography variant="body2" color="text.secondary" mb={2}>
        Total: <strong>{new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(total)}</strong>
      </Typography>

      <Stack direction="row" gap={1}>
        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
        <Button onClick={() => navigate({ to: "/payroll-l2/expenses" as any })}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!canSubmit || createMut.isPending}
          onClick={() => createMut.mutate()}
        >
          Submit Report
        </Button>
      </Stack>
    </Box>
  );
}
